"use client";

import {
  Clock,
  DollarSign,
  MessageSquare,
  PieChart,
  ThumbsUp,
  Users,
  Brain,
  BarChart,
} from "lucide-react";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/shadcnComponents/ui/card";
import { Progress } from "@/shadcnComponents/ui/progress";
import { Badge } from "@/shadcnComponents/ui/badge";

// Add Aaina's data to the competitorData object
const competitorData = {
  // Original generic data
  overview: {
    mostMentioned: {
      name: "Zepto",
      mentions: 14,
      percentage: 50,
    },
    totalMentions: 28,
    keyAdvantage: {
      feature: "Speed",
      description: "20-point lead over competitors",
    },
  },
  competitors: [
    { name: "Zepto", mentions: 14, color: "#7C3AED", percentage: 50 },
    { name: "Swiggy", mentions: 6, color: "#FC8019", percentage: 21 },
    { name: "Zomato", mentions: 5, color: "#CB202D", percentage: 18 },
    { name: "Blinkit", mentions: 3, color: "#FFD300", percentage: 11 },
  ],
  comparisonFactors: [
    {
      factor: "Speed of Delivery",
      leader: "Zepto",
      score: "85/100",
      comment:
        "Zepto has really taken the lead in solving the challenge of 10-minute food delivery—a goal Zomato and Swiggy initially set out to achieve.",
    },
    {
      factor: "Food Freshness",
      leader: "Zomato",
      score: "75/100",
      comment:
        "The garlic bread didn't seem very fresh to me. Instead, it was pretty cold.",
    },
    {
      factor: "Value Perception",
      leader: "Zepto",
      score: "72/100",
      comment:
        "The best part of Zepto is that it offers breakfast at pocket-friendly prices, unlike Zomato and Swiggy where delivery charges and GST increase the total cost.",
    },
  ],

  // Add Aaina's data
  aaina: {
    overview: {
      mostMentioned: {
        name: "LIC",
        mentions: 1,
        percentage: 100,
      },
      totalMentions: 1,
      keyAdvantage: {
        feature: "Physical Policy Documents",
        description:
          "Customers prefer physical documents for trust and security",
      },
    },
    competitors: [
      { name: "LIC", mentions: 1, color: "#1E88E5", percentage: 100 },
    ],
    comparisonFactors: [
      {
        factor: "Physical Policy Documents",
        leader: "LIC",
        score: "High Trust",
        comment:
          "Absolutely agree. In the insurance sector, companies like LIC still provide physical policy documents, which many senior citizens prefer for trust and security.",
      },
    ],
    competitorInsights: [
      {
        name: "LIC",
        color: "#1E88E5",
        initials: "LIC",
        keyLearning: "Physical documentation builds trust with senior citizens",
        praisePoints:
          "Provision of physical policy documents which align with trust preferences of senior citizens.",
        critiquePoints: "None mentioned in the comments.",
        audienceInclination:
          "Users who prioritize physical proof and traditional methods.",
        exampleComments: [
          "Absolutely agree. In the insurance sector, companies like LIC still provide physical policy documents, which many senior citizens prefer for trust and security.",
        ],
      },
    ],
    keyTakeaways: [
      "Stable Money is appreciated for its efforts to bridge the gap between digital convenience and physical security.",
      "Maintaining and expanding the approach of blending digital and physical proof could enhance customer trust further.",
      "Consider continuously evaluating and expanding offerings to include more hybrid solutions that cater to both digital-savvy users and those who prefer physical documentation.",
      "Understanding and addressing varied trust preferences across different generations is crucial in the fintech industry.",
    ],
    noCompetitorsMessage: "",
    exampleComments: [
      "Absolutely agree. In the insurance sector, companies like LIC still provide physical policy documents, which many senior citizens prefer for trust and security.",
    ],
    overallInsight:
      "Customers perceive LIC as trustworthy due to its provision of physical policy documents, which aligns with the trust preferences of senior citizens. This is an area where Stable Money is also recognized for its efforts to bridge the gap between digital and physical trust.",
  },
  // Update Richa Arora's data based on the provided content
  richa: {
    overview: {
      mostMentioned: {
        name: "None",
        mentions: 0,
        percentage: 0,
      },
      totalMentions: 0,
      keyAdvantage: {
        feature: "Physical FD Receipts",
        description:
          "Senior citizens prefer physical receipts for trust and security",
      },
    },
    competitors: [],
    comparisonFactors: [],
    competitorInsights: [],
    keyTakeaways: [
      "Stable Money's initiative to offer physical FD receipts for senior citizens was highly praised as thoughtful and customer-first innovation.",
      "Maintaining this customer-centric approach and continuing to identify unique needs could further strengthen the brand's market position.",
      "Consider exploring other areas where traditional methods can be blended with digital solutions to enhance trust and comfort, especially for older generations.",
      "Continue to prioritize understanding and addressing customer needs to foster loyalty and satisfaction.",
    ],
    noCompetitorsMessage:
      "There were no mentions of competitors in the comments, indicating that the focus was solely on the initiative by Stable Money.",
    exampleComments: [],
    overallInsight:
      "The initiative to offer physical FD receipts for senior citizens demonstrates Stable Money's understanding of varied customer needs across different age groups. This approach bridges the gap between digital convenience and the security that physical documentation provides, which is particularly valued by older customers.",
  },

  // Add Gunjan Mishra's data
  gunjanMishra: {
    overview: {
      mostMentioned: {
        name: "None",
        mentions: 0,
        percentage: 0,
      },
      totalMentions: 0,
      keyAdvantage: {
        feature: "No Competitors Mentioned",
        description: "Focus on product benefits rather than competition",
      },
    },
    competitors: [],
    comparisonFactors: [],
    competitorInsights: [],
    keyTakeaways: [
      "The narrative around WachMe resonated strongly with the audience, focusing on the safety, local manufacturing, and practical utility for working parents",
      "While no competitors were mentioned, it may be valuable to proactively recognize potential competitors in future marketing strategies",
      "Continue highlighting unique selling points such as safety without social media, local manufacturing, and support for work-life balance",
      "Consider expanding customer education on the broader applications of WachMe across different segments",
      "The comments primarily focused on validating the effectiveness and emotional impact of WachMe for working parents",
    ],
    noCompetitorsMessage:
      "There are no competitor mentions in the comments section. This indicates a strong focus on the WachMe product, rather than a discussion on the competitive landscape.",
    exampleComments: [
      "Beautiful",
      "Very good",
      "It's wonderful to hear how WachMe has made such a positive impact on your life as a working mom.",
    ],
    overallInsight:
      "The comments primarily focused on validating the effectiveness and emotional impact of WachMe for working parents, without introducing any discussion about competitors. This suggests a highly engaged and supportive audience drawn towards the narrative of local innovation and practical utility. Continue leveraging these strengths to build community support and customer loyalty.",
  },

  // Pankhuri's specific data (formerly Ravi's)
  pankhuri: {
    overview: {
      mostMentioned: {
        name: "None",
        mentions: 0,
        percentage: 0,
      },
      totalMentions: 0,
      keyAdvantage: {
        feature: "No Competitors Mentioned",
        description: "No direct comparisons in comments",
      },
    },
    competitors: [],
    comparisonFactors: [],
    competitorInsights: [],
    keyTakeaways: [
      "The event was perceived as exciting and a great opportunity, with many comments showing interest and appreciation.",
      "No competitors were mentioned or comparisons made in the comments.",
      "Continue promoting the unique benefits and exciting opportunities associated with the Tata Crucible Corporate Quiz.",
      "Focus on maintaining audience interest and engagement through the unique value proposition.",
      "The absence of competitor mentions suggests a strong and unique market position.",
    ],
    noCompetitorsMessage:
      "No competitors were mentioned in the comments for this content. The Tata Crucible Corporate Quiz appears to be perceived as a unique opportunity without direct comparisons to similar events or services.",
  },

  // Shivangi's specific data (formerly Shreya's)
  shivangi: {
    overview: {
      mostMentioned: {
        name: "None",
        mentions: 0,
        percentage: 0,
      },
      totalMentions: 0,
      keyAdvantage: {
        feature: "No Competitors Mentioned",
        description: "No direct comparisons in comments",
      },
    },
    competitors: [],
    comparisonFactors: [],
    competitorInsights: [],
    keyTakeaways: [
      "The event was perceived as exciting and a great opportunity, with many comments showing interest and appreciation.",
      "No competitors were mentioned or comparisons made in the comments.",
      "Continue promoting the unique benefits and exciting opportunities associated with the Tata Crucible Corporate Quiz.",
      "Focus on maintaining audience interest and engagement through the unique value proposition.",
    ],
    noCompetitorsMessage:
      "No competitors were mentioned in the comments for this content. The Tata Crucible Corporate Quiz appears to be perceived as a unique opportunity without direct comparisons to similar events or services.",
  },

  // New influencer data - Arjun
  arjun: {
    overview: {
      mostMentioned: {
        name: "Swiggy",
        mentions: 6,
        percentage: 40,
      },
      totalMentions: 15,
      keyAdvantage: {
        feature: "Corporate Solutions",
        description: "Better suited for office environments",
      },
    },
    competitors: [
      { name: "Swiggy", mentions: 6, color: "#FC8019", percentage: 40 },
      { name: "Zomato", mentions: 4, color: "#CB202D", percentage: 26.7 },
      { name: "FreshMenu", mentions: 3, color: "#7AC142", percentage: 20 },
      { name: "Box8", mentions: 2, color: "#FF8C00", percentage: 13.3 },
    ],
    comparisonFactors: [
      {
        factor: "Corporate Ordering",
        leader: "Zepto",
        score: "85/100",
        comment:
          "Zepto's quick delivery makes it ideal for impromptu office meetings and team breakfasts.",
      },
      {
        factor: "Bulk Order Handling",
        leader: "Swiggy",
        score: "80/100",
        comment:
          "Swiggy's corporate solutions are more mature, but Zepto is catching up quickly.",
      },
      {
        factor: "Invoicing",
        leader: "Zomato",
        score: "75/100",
        comment:
          "Zomato provides better GST invoicing for corporate expenses, something Zepto needs to improve.",
      },
      {
        factor: "Consistency",
        leader: "FreshMenu",
        score: "82/100",
        comment:
          "FreshMenu has more consistent quality for large orders, but Zepto has better delivery times.",
      },
    ],
    competitorInsights: [
      {
        name: "Swiggy",
        color: "#FC8019",
        initials: "SW",
        keyLearning: "Established corporate solutions but slower delivery",
        praisePoints:
          "Dedicated corporate accounts and bulk ordering features.",
        critiquePoints: "Delivery times too long for urgent office needs.",
        audienceInclination:
          "Larger companies with planned catering requirements.",
        exampleComments: [
          "Swiggy's corporate dashboard is better for expense tracking, but delivery is too slow for morning meetings.",
          "We've switched most of our impromptu orders to Zepto while keeping Swiggy for planned events.",
        ],
      },
      {
        name: "Zomato",
        color: "#CB202D",
        initials: "ZM",
        keyLearning:
          "Good for expense management but limited breakfast options",
        praisePoints: "Proper GST invoicing and expense categorization.",
        critiquePoints: "Limited breakfast options for office environments.",
        audienceInclination:
          "Finance-conscious corporate users who prioritize proper expense documentation.",
        exampleComments: [
          "Zomato's invoicing system works better with our expense software.",
          "Their corporate discount program is good but morning delivery times are too unpredictable for meetings.",
        ],
      },
      {
        name: "FreshMenu",
        color: "#7AC142",
        initials: "FM",
        keyLearning: "Consistent quality but limited coverage",
        praisePoints: "Standardized portions ideal for team distribution.",
        critiquePoints: "Limited delivery areas and longer preparation times.",
        audienceInclination:
          "Offices looking for consistent, portion-controlled meals.",
        exampleComments: [
          "FreshMenu's consistency is great for team lunches, but they don't deliver to all our office locations.",
          "Their meal boxes are perfect for distribution but Zepto's speed is better for urgent needs.",
        ],
      },
    ],
    keyTakeaways: [
      "Corporate features like bulk ordering and proper invoicing should be prioritized for development.",
      "Speed remains a key advantage for office environments with urgent meeting needs.",
      "Consistency in portion sizes is important for team distribution and should be standardized.",
      "Expanding coverage to business districts would capture more corporate customers.",
      "Developing scheduling features for recurring office orders would address a key market need.",
    ],
  },
  // Add Mansi's specific data to the competitorData object
  mansi: {
    overview: {
      mostMentioned: {
        name: "None",
        mentions: 0,
        percentage: 0,
      },
      totalMentions: 0,
      keyAdvantage: {
        feature: "No Competitors Mentioned",
        description: "No direct comparisons in comments",
      },
    },
    competitors: [],
    comparisonFactors: [],
    competitorInsights: [],
    keyTakeaways: [
      "The event itself generated excitement and was well-received by the audience.",
      "No competitor comparisons were made, indicating focus was on the event itself.",
      "Continue promoting the event and engaging with the audience to maintain excitement.",
      "Ensure smooth execution to further enhance the brand's reputation.",
      "Leverage the enthusiasm to drive participation and engagement.",
    ],
    noCompetitorsMessage:
      "No competitors were mentioned in the comments. The Tata Crucible Corporate Quiz appears to be perceived as a unique opportunity without direct comparisons to similar events or services.",
  },
  // Add Sonali's data to the competitorData object
  sonali: {
    overview: {
      mostMentioned: {
        name: "None",
        mentions: 0,
        percentage: 0,
      },
      totalMentions: 0,
      keyAdvantage: {
        feature: "No Competitors Mentioned",
        description: "No direct comparisons in comments",
      },
    },
    competitors: [],
    comparisonFactors: [],
    competitorInsights: [],
    keyTakeaways: [
      "The event itself generated excitement and was well-received by the audience.",
      "No competitor comparisons were made, indicating focus was on the event itself.",
      "Continue promoting the event and engaging with the audience to maintain excitement.",
      "Ensure smooth execution to further enhance the brand's reputation.",
      "Leverage the enthusiasm to drive participation and engagement.",
    ],
    noCompetitorsMessage:
      "No competitors were mentioned in the comments. The Tata Crucible Corporate Quiz appears to be perceived as a unique opportunity without direct comparisons to similar events or services.",
  },
};

// Modify the CompetitorsValues component to use the appropriate data
export function CompetitorsValues({ influencer = null }) {
  // Use appropriate data based on the selected influencer
  let data;

  if (influencer === 0) {
    // Use Aaina's data for first influencer
    data = competitorData.aaina;
  } else if (influencer === 1) {
    // Use Richa's data for second influencer
    data = competitorData.richa;
  } else {
    data = competitorData;
  }

  // Check if there are no competitors
  const hasNoCompetitors = data.competitors && data.competitors.length === 0;

  return (
    <div className="space-y-6">
      {/* Overview Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card className="bg-gradient-to-br from-[#E7F3FF] to-white border-[#C7E6FF]">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-[#0A66C2]">
              Most Mentioned Competitor
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex items-end justify-between">
              <div>
                <div className="text-2xl font-bold text-[#191919]">
                  {data.overview.mostMentioned.name}
                </div>
                <div className="text-sm text-[#666666]">
                  {data.overview.mostMentioned.mentions} mentions (
                  {data.overview.mostMentioned.percentage}%)
                </div>
              </div>
              {!hasNoCompetitors && (
                <div className="h-10 w-10 rounded-full bg-[#1E88E5] flex items-center justify-center text-white">
                  {data.overview.mostMentioned.name === "Swiggy"
                    ? "S"
                    : data.overview.mostMentioned.name === "Zomato"
                    ? "Z"
                    : data.overview.mostMentioned.name === "LIC"
                    ? "L"
                    : "N"}
                </div>
              )}
            </div>
          </CardContent>
        </Card>

        <Card className="bg-gradient-to-br from-[#E7F3FF] to-white border-[#C7E6FF]">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-[#0A66C2]">
              Total Competitor Mentions
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex items-end justify-between">
              <div className="text-2xl font-bold text-[#191919]">
                {data.overview.totalMentions}
              </div>
              {!hasNoCompetitors && (
                <div className="flex gap-2">
                  {data.competitors.map((comp, index) => (
                    <div
                      key={index}
                      className="h-6 w-6 rounded-full flex items-center justify-center text-white text-xs"
                      style={{ backgroundColor: comp.color }}
                    >
                      {comp.mentions}
                    </div>
                  ))}
                </div>
              )}
            </div>
          </CardContent>
        </Card>

        <Card className="bg-gradient-to-br from-[#E7F3FF] to-white border-[#C7E6FF]">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-[#0A66C2]">
              Key Competitive Advantage
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex items-end justify-between">
              <div>
                <div className="text-2xl font-bold text-[#191919]">
                  {data.overview.keyAdvantage.feature}
                </div>
                <div className="text-sm text-[#666666]">
                  {data.overview.keyAdvantage.description}
                </div>
              </div>
              <div className="h-10 w-10 rounded-full bg-[#E7F3FF] flex items-center justify-center">
                <Clock className="h-5 w-5 text-[#0A66C2]" />
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* No Competitors Message */}
      {hasNoCompetitors && (
        <Card className="border-[#C7E6FF]">
          <CardHeader>
            <CardTitle className="text-[#0A66C2]">
              No Competitor Mentions
            </CardTitle>
            <CardDescription>
              Analysis of competitor mentions in comments
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="flex items-center gap-4 mb-6">
              <div className="h-12 w-12 rounded-full bg-[#E7F3FF] flex items-center justify-center">
                <PieChart className="h-6 w-6 text-[#0A66C2]" />
              </div>
              <p className="text-[#666666]">{data.noCompetitorsMessage}</p>
            </div>

            {/* Example Comments */}
            {data.exampleComments && data.exampleComments.length > 0 && (
              <div className="rounded-lg bg-[#F8FAFF] p-4 mt-4 mb-4">
                <h3 className="text-lg font-semibold text-[#0A66C2] mb-3">
                  Example Comments
                </h3>
                <ul className="space-y-2 text-[#666666]">
                  {data.exampleComments.map((comment, index) => (
                    <li key={index} className="flex items-start gap-2">
                      <div className="mt-1 h-5 w-5 rounded-full bg-[#E7F3FF] flex items-center justify-center text-[#0A66C2]">
                        {index + 1}
                      </div>
                      <p>"{comment}"</p>
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {/* Overall Insight */}
            {data.overallInsight && (
              <div className="rounded-lg bg-[#F8FAFF] p-4 mt-4">
                <h3 className="text-lg font-semibold text-[#0A66C2] mb-3">
                  Overall Insights
                </h3>
                <p className="text-[#666666] mb-4">{data.overallInsight}</p>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Mentions Analysis - Only show if there are competitors */}
      {!hasNoCompetitors && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <Card className="border-[#C7E6FF]">
            <CardHeader>
              <CardTitle className="text-[#0A66C2]">
                Competitor Mentions Distribution
              </CardTitle>
              <CardDescription>
                Breakdown of all competitors mentioned in comments
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              {data.competitors.map((competitor, index) => (
                <div key={index} className="space-y-2">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <div
                        className="h-3 w-3 rounded-full"
                        style={{ backgroundColor: competitor.color }}
                      ></div>
                      <span className="font-medium">{competitor.name}</span>
                      {index === 0 && (
                        <Badge className="bg-[#E7F3FF] text-[#0A66C2] hover:bg-[#C7E6FF]">
                          Most Mentioned
                        </Badge>
                      )}
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="text-sm">
                        {competitor.mentions} mentions
                      </span>
                      <span className="text-sm text-[#666666]">
                        ({competitor.percentage}%)
                      </span>
                    </div>
                  </div>
                  <Progress
                    value={competitor.percentage}
                    className="h-2"
                    indicatorClassName="transition-all"
                    style={{ backgroundColor: "rgba(0,0,0,0.05)" }}
                    color={competitor.color}
                  />
                </div>
              ))}
            </CardContent>
          </Card>

          <Card className="border-[#C7E6FF]">
            <CardHeader>
              <CardTitle className="text-[#0A66C2]">Share of Voice</CardTitle>
              <CardDescription>
                Proportional representation of competitor mentions
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="flex justify-center mb-6">
                <div className="relative h-60 w-60">
                  <PieChart className="h-full w-full text-[#E7F3FF]" />
                  <div className="absolute inset-0 flex items-center justify-center">
                    <div className="text-center">
                      <div className="text-xl font-bold text-[#0A66C2]">
                        {data.overview.totalMentions}
                      </div>
                      <div className="text-xs text-[#666666]">
                        Total Mentions
                      </div>
                    </div>
                  </div>
                  <div className="absolute top-6 right-6 space-y-3">
                    {data.competitors.map((competitor, index) => (
                      <div key={index} className="flex items-center gap-2">
                        <div
                          className="h-3 w-3 rounded-full"
                          style={{ backgroundColor: competitor.color }}
                        ></div>
                        <span className="text-xs">{competitor.name}</span>
                        <span className="text-xs text-[#666666]">
                          {competitor.percentage}%
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
              <p className="text-center text-sm text-[#666666]">
                <span className="text-[#0A66C2] font-medium">
                  {data.overview.mostMentioned.name}
                </span>{" "}
                dominates the conversation with{" "}
                <span className="text-[#0A66C2] font-medium">
                  {data.overview.mostMentioned.percentage}%
                </span>{" "}
                of all competitor mentions in the comments.
              </p>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Comparison Factors - Only show if there are competitors */}
      {!hasNoCompetitors &&
        data.comparisonFactors &&
        data.comparisonFactors.length > 0 && (
          <div className="space-y-4">
            <h2 className="text-2xl font-semibold text-[#191919] flex items-center gap-2">
              <BarChart className="h-6 w-6 text-[#0A66C2]" />
              Comparison Factors
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              {data.comparisonFactors.map((factor, index) => (
                <Card key={index} className="bg-[#F8FAFF]">
                  <CardHeader className="pb-3">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2 text-[#0A66C2]">
                        {factor.factor === "Speed of Delivery" ||
                        factor.factor === "Speed" ? (
                          <Clock className="h-5 w-5" />
                        ) : factor.factor === "Food Freshness" ||
                          factor.factor === "Service" ? (
                          <ThumbsUp className="h-5 w-5" />
                        ) : (
                          <DollarSign className="h-5 w-5" />
                        )}
                        <CardTitle>{factor.factor}</CardTitle>
                      </div>
                      <Badge
                        className={`hover:${
                          factor.leader === "Zepto"
                            ? "bg-[#7C3AED]"
                            : factor.leader === "Swiggy"
                            ? "bg-[#FC8019]"
                            : factor.leader === "LIC"
                            ? "bg-[#1E88E5]"
                            : "bg-[#CB202D]"
                        }`}
                        style={{
                          backgroundColor:
                            factor.leader === "Zepto"
                              ? "#7C3AED"
                              : factor.leader === "Swiggy"
                              ? "#FC8019"
                              : factor.leader === "LIC"
                              ? "#1E88E5"
                              : "#CB202D",
                        }}
                      >
                        {factor.leader}
                      </Badge>
                    </div>
                    <CardDescription>
                      Leader: {factor.leader} ({factor.score})
                    </CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className="text-sm text-[#666666]">
                      <div className="flex items-center gap-1 mb-1">
                        <MessageSquare className="h-3 w-3 text-[#0A66C2]" />
                        <span className="font-medium">
                          Comment from LinkedIn:
                        </span>
                      </div>
                      <p className="italic text-[#191919]">
                        "{factor.comment}"
                      </p>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        )}

      {/* Competitor Learnings & Audience Inclination - Only show if there are competitors */}
      {!hasNoCompetitors &&
        data.competitorInsights &&
        data.competitorInsights.length > 0 && (
          <div className="space-y-4">
            <h2 className="text-2xl font-semibold text-[#191919] flex items-center gap-2">
              <Users className="h-6 w-6 text-[#0A66C2]" />
              Competitor Learnings & Audience Inclination
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {data.competitorInsights.map((competitor, index) => (
                <Card
                  key={index}
                  className="border-t-4"
                  style={{ borderTopColor: competitor.color }}
                >
                  <CardHeader className="pb-3">
                    <div className="flex items-center justify-between">
                      <CardTitle className="text-xl">
                        {competitor.name}
                      </CardTitle>
                      <div
                        className="h-8 w-8 rounded-full flex items-center justify-center text-white"
                        style={{ backgroundColor: competitor.color }}
                      >
                        {competitor.initials}
                      </div>
                    </div>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div>
                      <div className="flex items-center gap-2 text-[#0A66C2] mb-2">
                        <Brain className="h-4 w-4" />
                        <h4 className="font-medium">Key Learnings</h4>
                      </div>
                      <p className="text-sm">{competitor.keyLearning}</p>
                    </div>

                    <div className="grid grid-cols-2 gap-2">
                      <div className="rounded-lg bg-[#E7F3FF] p-3">
                        <div className="flex items-center gap-2 text-[#0A66C2] mb-1">
                          <ThumbsUp className="h-3 w-3" />
                          <h5 className="text-xs font-medium">Praise Points</h5>
                        </div>
                        <p className="text-xs">{competitor.praisePoints}</p>
                      </div>
                      <div className="rounded-lg bg-slate-50 p-3">
                        <div className="flex items-center gap-2 text-slate-600 mb-1">
                          <ThumbsUp className="h-3 w-3 rotate-180" />
                          <h5 className="text-xs font-medium">
                            Critique Points
                          </h5>
                        </div>
                        <p className="text-xs">{competitor.critiquePoints}</p>
                      </div>
                    </div>

                    <div className="rounded-lg bg-[#E7F3FF] p-3">
                      <div className="flex items-center gap-2 text-[#0A66C2] mb-1">
                        <Users className="h-3 w-3" />
                        <h5 className="text-xs font-medium">
                          Audience Inclination
                        </h5>
                      </div>
                      <p className="text-xs">
                        {competitor.audienceInclination}
                      </p>
                    </div>

                    {competitor.exampleComments &&
                      competitor.exampleComments.length > 0 && (
                        <div className="rounded-lg bg-slate-50 p-3">
                          <div className="flex items-center gap-2 text-slate-600 mb-1">
                            <MessageSquare className="h-3 w-3" />
                            <h5 className="text-xs font-medium">
                              Example Comments
                            </h5>
                          </div>
                          {competitor.exampleComments.map((comment, idx) => (
                            <p key={idx} className="text-xs italic mt-1">
                              "{comment}"
                            </p>
                          ))}
                        </div>
                      )}
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        )}

      {/* Key Takeaways */}
      <Card className="border-[#C7E6FF]">
        <CardHeader>
          <CardTitle className="text-[#0A66C2]">
            Key Takeaways & Recommendations
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            {data?.keyTakeaways?.map((takeaway, index) => (
              <div key={index} className="flex items-start gap-3">
                <div className="mt-0.5 h-6 w-6 rounded-full bg-[#E7F3FF] flex items-center justify-center text-[#0A66C2]">
                  {index + 1}
                </div>
                <p className="text-[#191919]">{takeaway}</p>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
